import { useState, useEffect } from 'react';
import { Button, Select } from "@repo/o37-ui";
import {
  RefreshCw,
  Check,
  AlertCircle,
  Loader2,
  TrendingUp,
  X
} from "lucide-react";
import { HoldingsCompany, formatCents } from '../../types';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface DetectedPattern {
  vendor: string;
  occurrences: number;
  avgAmountCents: number;
  frequency: string;
  intervalDays: number;
  firstDate: string;
  lastDate: string;
  transactionIds: string[];
  selected?: boolean;
  companyId?: string;
  category?: string;
}

interface SubscriptionPatternModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPatternsCreated: () => void;
  companies: HoldingsCompany[];
}

const EXPENSE_CATEGORIES = [
  { key: 'software', name: 'Software & SaaS' },
  { key: 'cloud', name: 'Cloud & Infrastructure' },
  { key: 'tools', name: 'Tools & Services' },
  { key: 'marketing', name: 'Marketing' },
  { key: 'payroll', name: 'Payroll & HR' },
  { key: 'legal', name: 'Legal & Compliance' },
  { key: 'office', name: 'Office & Supplies' },
  { key: 'uncategorized', name: 'Uncategorized' }
];

export default function SubscriptionPatternModal({
  isOpen,
  onClose,
  onPatternsCreated,
  companies
}: SubscriptionPatternModalProps) {
  const [patterns, setPatterns] = useState<DetectedPattern[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    if (isOpen) {
      detectPatterns();
    }
  }, [isOpen]);

  const detectPatterns = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/holdings/subscriptions/detect-patterns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify({ minOccurrences: 2 })
      });

      if (response.ok) {
        const data = await response.json();
        // Default all patterns to selected
        setPatterns(data.patterns.map((p: DetectedPattern) => ({
          ...p,
          selected: true,
          category: 'uncategorized'
        })));
      } else {
        throw new Error('Failed to detect patterns');
      }
    } catch (e) {
      console.error('Failed to detect patterns:', e);
      setError('Failed to analyze transactions for patterns');
    } finally {
      setLoading(false);
    }
  };

  const togglePattern = (index: number) => {
    setPatterns(prev => prev.map((p, i) =>
      i === index ? { ...p, selected: !p.selected } : p
    ));
  };

  const updatePattern = (index: number, field: string, value: string) => {
    setPatterns(prev => prev.map((p, i) =>
      i === index ? { ...p, [field]: value } : p
    ));
  };

  const selectAll = () => {
    setPatterns(prev => prev.map(p => ({ ...p, selected: true })));
  };

  const deselectAll = () => {
    setPatterns(prev => prev.map(p => ({ ...p, selected: false })));
  };

  const createSelectedSubscriptions = async () => {
    const selectedPatterns = patterns.filter(p => p.selected);
    if (selectedPatterns.length === 0) return;

    setCreating(true);
    setError(null);

    try {
      let created = 0;
      for (const pattern of selectedPatterns) {
        const response = await fetch(`${API_URL}/holdings/subscriptions/create-from-pattern`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-user-id': userId,
            'x-user-role': userRole
          },
          body: JSON.stringify({
            vendorName: pattern.vendor,
            costCents: pattern.avgAmountCents,
            billingCycle: pattern.frequency,
            transactionIds: pattern.transactionIds,
            companyId: pattern.companyId || null,
            category: pattern.category || 'uncategorized'
          })
        });

        if (response.ok) {
          created++;
        }
      }

      if (created > 0) {
        onPatternsCreated();
        onClose();
      } else {
        setError('Failed to create subscriptions');
      }
    } catch (e) {
      console.error('Failed to create subscriptions:', e);
      setError('Failed to create subscriptions');
    } finally {
      setCreating(false);
    }
  };

  const getFrequencyLabel = (freq: string) => {
    const labels: Record<string, string> = {
      weekly: 'Weekly',
      biweekly: 'Bi-weekly',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      yearly: 'Yearly'
    };
    return labels[freq] || freq;
  };

  const selectedCount = patterns.filter(p => p.selected).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-100">
          <h2 className="text-xl font-semibold">Detected Recurring Payments</h2>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-lg">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
        {/* Header info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <TrendingUp className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Auto-detect subscriptions</p>
              <p>
                We analyze your unmatched transactions to find recurring payments.
                Select the ones you want to track as subscriptions.
              </p>
            </div>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-red-800">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">{error}</span>
            </div>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin mb-4" />
            <p className="text-neutral-600">Analyzing transactions...</p>
          </div>
        )}

        {/* No patterns found */}
        {!loading && patterns.length === 0 && !error && (
          <div className="text-center py-12 text-neutral-500">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
            <h3 className="text-lg font-medium mb-2">No Patterns Found</h3>
            <p className="mb-4">
              We couldn't detect any recurring payment patterns in your unmatched transactions.
            </p>
            <Button
              variant="outline"
              onClick={detectPatterns}
              icon={<RefreshCw className="w-4 h-4" />}
              text="Try Again"
            />
          </div>
        )}

        {/* Patterns list */}
        {!loading && patterns.length > 0 && (
          <>
            {/* Selection controls */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-neutral-600">
                Found <strong>{patterns.length}</strong> potential subscriptions
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="text-xs h-8"
                  onClick={selectAll}
                  text="Select All"
                />
                <Button
                  variant="outline"
                  className="text-xs h-8"
                  onClick={deselectAll}
                  text="Deselect All"
                />
                <Button
                  variant="outline"
                  className="text-xs h-8"
                  onClick={detectPatterns}
                  icon={<RefreshCw className="w-3.5 h-3.5" />}
                  text="Refresh"
                />
              </div>
            </div>

            {/* Patterns table */}
            <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
              {patterns.map((pattern, index) => (
                <div
                  key={pattern.vendor}
                  className={`p-4 ${pattern.selected ? 'bg-blue-50/50' : 'bg-white'}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    <button
                      onClick={() => togglePattern(index)}
                      className={`w-5 h-5 rounded border flex items-center justify-center mt-0.5 ${
                        pattern.selected
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'border-neutral-300 bg-white'
                      }`}
                    >
                      {pattern.selected && <Check className="w-3.5 h-3.5" />}
                    </button>

                    {/* Pattern details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-neutral-900 truncate">
                          {pattern.vendor}
                        </h4>
                        <span className="text-lg font-semibold text-neutral-900 ml-4">
                          {formatCents(pattern.avgAmountCents)}
                          <span className="text-sm font-normal text-neutral-500">
                            /{pattern.frequency}
                          </span>
                        </span>
                      </div>

                      <div className="flex items-center gap-4 text-sm text-neutral-500 mb-3">
                        <span>{pattern.occurrences} occurrences</span>
                        <span>|</span>
                        <span>{getFrequencyLabel(pattern.frequency)}</span>
                        <span>|</span>
                        <span>
                          {new Date(pattern.firstDate).toLocaleDateString()} - {new Date(pattern.lastDate).toLocaleDateString()}
                        </span>
                      </div>

                      {/* Assignment options (only when selected) */}
                      {pattern.selected && (
                        <div className="flex gap-4 mt-3">
                          <div className="flex-1">
                            <label className="text-xs text-neutral-500 mb-1 block">Company (optional)</label>
                            <Select
                              value={pattern.companyId || ''}
                              onChange={(e) => updatePattern(index, 'companyId', e.target.value)}
                              className="text-sm h-8"
                            >
                              <option value="">No company</option>
                              {companies.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                              ))}
                            </Select>
                          </div>
                          <div className="flex-1">
                            <label className="text-xs text-neutral-500 mb-1 block">Category</label>
                            <Select
                              value={pattern.category || 'uncategorized'}
                              onChange={(e) => updatePattern(index, 'category', e.target.value)}
                              className="text-sm h-8"
                            >
                              {EXPENSE_CATEGORIES.map(c => (
                                <option key={c.key} value={c.key}>{c.name}</option>
                              ))}
                            </Select>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button
            variant="outline"
            onClick={onClose}
            text="Cancel"
          />
          <Button
            onClick={createSelectedSubscriptions}
            disabled={selectedCount === 0 || creating}
            icon={creating ? <Loader2 className="w-4 h-4 animate-spin" /> : undefined}
            text={creating ? 'Creating...' : `Create ${selectedCount} Subscription${selectedCount !== 1 ? 's' : ''}`}
          />
        </div>
        </div>
      </div>
    </div>
  );
}
